import styled from "@emotion/styled"
import type { DealershipInfo } from "@porsche-customer/slfinrtl-context-types"
import { PIcon, PTabsBar, PTag, PText, PWordmark } from "@porsche-design-system/components-react"
import {
	breakpoint,
	colorContrastMedium,
	getFocusVisibleStyle,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import { useCallback, useMemo, useState } from "react"
import type { JSX } from "react"
import { Link, useLocation } from "react-router"
import { NewBadge } from "../badge/NewBadge"
import { ContentWrapper } from "../contentWrapper/ContentWrapper"
import { Tooltip } from "../tooltip/Tooltip"
import { DisabledItem } from "./DisabledItem"
import type { NavItem, NestedNavItem } from "./NavItem"
import { Submenu } from "./Submenu"
import { mainNavigationHeightDesktop, mainNavigationHeightMobile } from "./mainNavigationHeight.const"

export interface MainNavigationProps {
	dealershipInfo?: DealershipInfo
	displayName?: string
	isCarSalesLite?: boolean
	isRTL?: boolean
	logoLabel: string
	logoLinkUrl?: string
	metaChildren?: JSX.Element
	navItems: NestedNavItem[]
	onAnalyticsLinkClick?: () => void
	onPcssLinkClick?: () => void
	username?: string
}

interface Position {
	insetInlineStart: number
	top: number
}

const hasHover = window.matchMedia("(hover: hover)").matches

export const MainNavigation = ({
	dealershipInfo,
	displayName,
	isCarSalesLite,
	isRTL,
	logoLabel,
	logoLinkUrl,
	metaChildren,
	navItems,
	onAnalyticsLinkClick,
	onPcssLinkClick,
	username,
}: MainNavigationProps) => {
	const [menuPosition, setMenuPosition] = useState<Position | null>(null)
	const [menuContent, setMenuContent] = useState<NavItem[]>([])

	const { pathname } = useLocation()

	// Don't create 2-level navigation items with only one subitem
	// Always set the url of the top-level navigation item to its first childs url
	const items: NestedNavItem[] = useMemo(
		() =>
			navItems.map((item) =>
				item.childNav?.length === 1
					? item.childNav[0]
					: item.childNav && item.childNav.length > 1
						? // search for the first child that has an url. Set the parent url to this url
							{ ...item, url: item.childNav.find((i) => typeof i.url === "string")?.url ?? item.url }
						: item,
			),
		[navItems],
	)

	// Sorted longest-url-first so the first prefix match found is always the most specific one
	const navItemUrlTabIndexEntries = useMemo<{ idx: number; url: string }[]>(() => {
		const itemUrls = new Map<string, number>()
		items
			.filter((item) => !item.isExternalUrl && typeof item.url === "string")
			.forEach((item, idx) => {
				itemUrls.set(item.url as string, idx)
				item.childNav
					?.filter((child) => !child.isExternalUrl && typeof child.url === "string")
					.forEach((child) => {
						itemUrls.set(child.url as string, idx)
					})
			})

		return [...itemUrls.entries()].map(([url, idx]) => ({ idx, url })).sort((a, b) => b.url.length - a.url.length)
	}, [items])

	const tabIndex = useMemo<number | undefined>(
		// Prefix-match so child routes (e.g. /vehicle-information/monroney-label) still
		// highlight their parent tab (/vehicle-information)
		() => navItemUrlTabIndexEntries.find(({ url }) => pathname === url || pathname.startsWith(`${url}/`))?.idx,
		[pathname, navItemUrlTabIndexEntries],
	)

	const openSubmenu = useCallback(
		(childNavItem: NavItem[]) => (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> & { target: HTMLElement }) => {
			if (childNavItem.length < 1) {
				return
			}
			setMenuContent(childNavItem)
			const rect = e.target.getBoundingClientRect()
			setMenuPosition({ insetInlineStart: isRTL ? window.innerWidth - rect.right : rect.left, top: rect.bottom })
		},
		[isRTL],
	)
	const hideMenu = useCallback(() => setMenuPosition(null), [])

	const checkMarginBufferAndHideSubmenu = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			const target = (e.target as HTMLElement).getBoundingClientRect()
			if (e.clientY + 12 > target.bottom && e.clientX > target.left && e.clientX < target.right) {
				return
			}
			hideMenu()
		},
		[hideMenu],
	)

	const openSubmenuOnTouch = useCallback(
		(childNavItem: NavItem[]) => (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> & { target: HTMLElement }) => {
			e.preventDefault()
			openSubmenu(childNavItem)(e)
		},
		[openSubmenu],
	)

	const onExternalUrlClick = useCallback(
		({ key }: NavItem) => {
			if (key === "pcss") {
				onPcssLinkClick?.()
			} else if (key === "analytics") {
				onAnalyticsLinkClick?.()
			}
		},
		[onPcssLinkClick, onAnalyticsLinkClick],
	)

	return (
		<Header as="header">
			{logoLinkUrl && (
				<LogoLink to={logoLinkUrl}>
					<PWordmark />
					<LogoLabel weight="bold">
						{logoLabel}
						{isCarSalesLite && (
							<LiteTag variant="primary" compact>
								Lite
							</LiteTag>
						)}
					</LogoLabel>
				</LogoLink>
			)}
			<Nav>
				<PTabsBar background="surface" activeTabIndex={tabIndex} compact>
					{items.map((navItem) =>
						navItem.isExternalUrl && typeof navItem.url === "string" ? (
							<ExternalLink
								key={navItem.key}
								target="_blank"
								href={navItem.url}
								onClick={() => {
									onExternalUrlClick(navItem)
									hideMenu()
								}}
							>
								{navItem.label}
								<ExternalIcon name="external" />
							</ExternalLink>
						) : typeof navItem.url === "string" ? (
							<Link
								key={navItem.key}
								data-testid={`${navItem.key}-has-submenu`}
								to={navItem.url}
								onMouseOver={navItem.childNav && hasHover ? openSubmenu(navItem.childNav) : undefined}
								onMouseOut={checkMarginBufferAndHideSubmenu}
								onClick={
									navItem.childNav
										? !hasHover
											? openSubmenuOnTouch(navItem.childNav)
											: undefined
										: hideMenu
								}
							>
								{navItem.label}
								{navItem.isNew && <StyledNewBadge compact />}
								{navItem.childNav && (
									<ChildNavArrowIcon
										name="arrow-head-down"
										color="inherit"
										aria={{ "aria-label": "Arrow Head Down icon" }}
									/>
								)}
							</Link>
						) : (
							<Link key={navItem.key} to="">
								<Tooltip content={navItem.url.disabledHint}>
									<DisabledItem data-testid={`${navItem.key}-disabled`}>{navItem.label}</DisabledItem>
								</Tooltip>
							</Link>
						),
					)}
				</PTabsBar>

				<LoginInformation>
					{metaChildren}

					{dealershipInfo?.locationCode && (
						<UsernameOrLocationCode>
							<PIcon name="pin" aria={{ "aria-label": "Pin icon" }} />
							<Tooltip placement="bottom" content={dealershipInfo.name}>
								<PText>{dealershipInfo.locationCode}</PText>
							</Tooltip>
						</UsernameOrLocationCode>
					)}
					{username && (
						<UsernameOrLocationCode>
							<PIcon name="user" aria={{ "aria-label": "User icon" }} />
							{displayName ? (
								<Tooltip placement="bottom" content={username}>
									<PText>{displayName}</PText>
								</Tooltip>
							) : (
								<PText>{username}</PText>
							)}
						</UsernameOrLocationCode>
					)}
				</LoginInformation>
			</Nav>
			{menuPosition !== null && (
				<Submenu
					data-testid="nav-submenu"
					menuContent={menuContent}
					top={menuPosition.top}
					insetInlineStart={menuPosition.insetInlineStart}
					pathname={pathname}
					onMouseLeave={hideMenu}
					onExternalUrlClick={onExternalUrlClick}
				/>
			)}
		</Header>
	)
}

const LogoLink = styled(Link)`
	display: flex;
	align-items: center;
	gap: ${spacingStaticLg};
	text-decoration: none;
	padding: ${spacingStaticSm};
	margin-inline: calc(-1 * ${spacingStaticSm});
	${getFocusVisibleStyle()}
`

const LogoLabel = styled(PText)`
	display: none;

	@media (min-width: ${breakpoint.l}px) {
		display: flex;
		gap: ${spacingStaticLg};

		&:before {
			content: "";
			width: 1px;
			background: ${colorContrastMedium};
		}
	}
`

const LiteTag = styled(PTag)`
	margin-inline-start: ${spacingStaticSm};
`

const ExternalLink = styled.a`
	align-self: start;
	white-space: nowrap;

	&:not(:last-child) {
		margin-inline-end: ${spacingStaticMd};
	}
`

const ExternalIcon = styled(PIcon)`
	margin-inline-start: ${spacingStaticXs};
`

const Header = styled(ContentWrapper)`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: ${spacingStaticSm};
	height: ${mainNavigationHeightMobile};

	@media (min-width: ${breakpoint.s}px) {
		gap: ${spacingStaticLg};
		flex-direction: row;
	}

	@media (min-width: ${breakpoint.m}px) {
		height: ${mainNavigationHeightDesktop};
	}
`

const Nav = styled.nav`
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: ${spacingStaticSm};

	@media (min-width: ${breakpoint.s}px) {
		flex: 1;
		flex-direction: row;
		justify-content: space-between;
	}
`

const StyledNewBadge = styled(NewBadge)`
	margin-inline: ${spacingStaticXs} -${spacingStaticSm};
	vertical-align: top;
`

const LoginInformation = styled.div`
	display: flex;
	gap: ${spacingStaticMd};
`

const UsernameOrLocationCode = styled.div`
	display: flex;
	gap: ${spacingStaticXs};
	white-space: nowrap;
`

const ChildNavArrowIcon = styled(PIcon)`
	padding-inline-start: 2px;
	pointer-events: none;
`
